import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { css, cx, sva } from "@/../styled-system/css";
import Check from "@/assets/svg/check.svg";
import ChevronDown from "@/assets/svg/chevron-down.svg";

import Button from "./Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command";
import { InputProps } from "./Input";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { ScrollArea } from "./ScrollArea";

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
    css?: object;
  };

const phoneInput = sva({
  slots: ["root", "input", "button", "flag"],
  base: {
    root: { display: "flex" },
    input: {
      roundedStartEnd: "lg",
      roundedEndEnd: "lg",
      roundedStartStart: "0",
      roundedEndStart: "0",
      borderLeft: "none",
      py: "2.5",
      px: "3.5",
      rounded: "lg",
      borderWidth: "1px",
      borderColor: "gray.300",
      width: "100%",
      ringColor: "brandBlue",
      _placeholder: {
        color: "gray.500",
      },
    },
    button: {
      display: "flex",
      gap: "1",
      alignItems: "center",
      justifyContent: "center",
      pl: "3.5",
      pr: "1",
      py: "0",
      roundedStartEnd: "0",
      roundedEndEnd: "0",
      roundedStartStart: "lg",
      roundedEndStart: "lg",
      borderWidth: "1px",
      borderRight: "none",
      borderColor: "gray.300",
      ringColor: "brandBlue",
    },
    flag: {
      display: "flex",
      h: "16px",
      w: "24px",
      overflow: "hidden",
      rounded: "sm",
      backgroundColor: "neutral.300",
    },
  },
});

const { root, input, button, flag } = phoneInput.raw();

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <input className={cx(className, css(input))} ref={ref} {...props} />
  ),
);
InputComponent.displayName = "InputComponent";

type CountrySelectOption = { label: string; value: RPNInput.Country };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
};

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const handleSelect = React.useCallback(
    (country: RPNInput.Country) => {
      onChange(country);
    },
    [onChange],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button width="fit" css={button} disabled={disabled}>
          <span className={css({ color: "gray.700", fontWeight: "normal" })}>
            {value}
          </span>
          <ChevronDown
            width={20}
            stroke="#98A2B3"
            display={disabled ? "none" : "visible"}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        css={{ width: "250px", p: "0", sm: { width: "300px" } }}
      >
        <Command>
          <CommandList>
            <ScrollArea css={{ h: "300px" }}>
              <CommandInput placeholder="Search country..." />
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup css={{ pr: "3" }}>
                {options
                  .filter((x) => x.value)
                  .map((option) => (
                    <CommandItem
                      css={{ gap: "2", cursor: "pointer" }}
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <FlagComponent
                        country={option.value}
                        countryName={option.label}
                      />
                      <span
                        className={css({
                          flex: "1",
                          fontSize: "sm",
                          lineHeight: "sm",
                        })}
                      >
                        {option.label}
                      </span>
                      {option.value && (
                        <span
                          className={css({ fontSize: "sm", lineHeight: "sm" })}
                        >
                          {`+${RPNInput.getCountryCallingCode(option.value)}`}
                        </span>
                      )}
                      <Check
                        width={16}
                        opacity={option.value === value ? "1" : "0"}
                        className={css({ ml: "auto" })}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className={css(flag)}>{Flag && <Flag title={countryName} />}</span>
  );
};
FlagComponent.displayName = "FlagComponent";

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    ({ css: cssProps = {}, onChange, ...props }, ref) => {
      const className = css(root, cssProps);
      return (
        <RPNInput.default
          ref={ref}
          className={className}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          inputComponent={InputComponent}
          defaultCountry="US"
          /**
           * Handles the onChange event.
           *
           * react-phone-number-input might trigger the onChange event as undefined
           * when a valid phone number is not entered. To prevent this,
           * the value is coerced to an empty string.
           *
           * @param {E164Number | undefined} value - The entered value
           */
          onChange={(value) => onChange?.(value || ("" as RPNInput.Value))}
          {...props}
        />
      );
    },
  );
PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
